import React, { useState, useEffect } from "react";
import { MdAttachMoney, MdFilterList } from "react-icons/md";
import SosialyServiceInstance from "service/sosialy.service";
import MembreServiceInstance from "service/membre.service";
import HeaderSection from "components/headers-section";
import ActionBar from "components/action-bar";
import SosialyTable from "./components/SosialyTable";
import SosialyFormModal from "./components/SosialyFormModal";
import DeleteConfirmationModal from "components/delete-confirm-modal";
import SosialyFilters from "./components/SosialyFilters";
import SosialyDetailModal from "./components/SosialyDetailModal";

const SosialyManager = () => {
  const [sosialy, setSosialy] = useState([]);
  const [membres, setMembres] = useState([]);
  const [filteredSosialy, setFilteredSosialy] = useState([]);
  const [loading, setLoading] = useState(false);
  const [membresLoading, setMembresLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [currentSosialy, setCurrentSosialy] = useState({
    id_membre: "",
    mois_sosialy: "",
    annee_sosialy: new Date().getFullYear().toString(),
    montant_sosialy: "",
    date_sosialy: new Date().toISOString().split("T")[0],
    objet_sosialy: "Cotisation mensuelle",
  });
  const [isEditing, setIsEditing] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    mois: "",
    annee: "",
    dateDebut: "",
    dateFin: "",
    montantMin: "",
    montantMax: "",
    membre: "",
  });

  const moisOptions = [
    "Janvier",
    "Février",
    "Mars",
    "Avril",
    "Mai",
    "Juin",
    "Juillet",
    "Août",
    "Septembre",
    "Octobre",
    "Novembre",
    "Décembre",
  ];

  useEffect(() => {
    fetchSosialy();
    fetchMembres();
  }, []);

  useEffect(() => {
    filterSosialy();
  }, [searchTerm, sosialy, filters]);

  const fetchSosialy = async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await SosialyServiceInstance.getAll();
      setSosialy(result || []);
      setFilteredSosialy(result || []);
    } catch (error) {
      setError(
        "Impossible de charger les cotisations. Veuillez réessayer plus tard."
      );
      console.error("Error fetching sosialy:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMembres = async () => {
    setMembresLoading(true);
    try {
      const result = await MembreServiceInstance.getAll();
      setMembres(result || []);
    } catch (error) {
      console.error("Error fetching membres:", error);
    } finally {
      setMembresLoading(false);
    }
  };

  const filterSosialy = () => {
    let filtered = [...sosialy];

    // Recherche textuelle
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (item) =>
          (item.objet_sosialy &&
            item.objet_sosialy.toLowerCase().includes(search)) ||
          (item.membre &&
            item.membre.nom_prenom_membre &&
            item.membre.nom_prenom_membre.toLowerCase().includes(search))
      );
    }

    // Filtre par mois
    if (filters.mois) {
      filtered = filtered.filter((item) => item.mois_sosialy === filters.mois);
    }

    // Filtre par année
    if (filters.annee) {
      filtered = filtered.filter(
        (item) => item.annee_sosialy === filters.annee
      );
    }

    // Filtre par date de début
    if (filters.dateDebut) {
      filtered = filtered.filter(
        (item) => new Date(item.date_sosialy) >= new Date(filters.dateDebut)
      );
    }

    // Filtre par date de fin
    if (filters.dateFin) {
      filtered = filtered.filter(
        (item) => new Date(item.date_sosialy) <= new Date(filters.dateFin)
      );
    }

    // Filtre par montant minimum
    if (filters.montantMin) {
      const min = parseFloat(filters.montantMin);
      if (!isNaN(min)) {
        filtered = filtered.filter(
          (item) => parseFloat(item.montant_sosialy) >= min
        );
      }
    }

    // Filtre par montant maximum
    if (filters.montantMax) {
      const max = parseFloat(filters.montantMax);
      if (!isNaN(max)) {
        filtered = filtered.filter(
          (item) => parseFloat(item.montant_sosialy) <= max
        );
      }
    }

    // Filtre par membre
    if (filters.membre) {
      filtered = filtered.filter(
        (item) =>
          item.membre &&
          item.membre.id_membre.toString() === filters.membre.toString()
      );
    }

    setFilteredSosialy(filtered);
  };

  const openCreateModal = () => {
    const currentDate = new Date();
    const currentMonth = moisOptions[currentDate.getMonth()];

    setCurrentSosialy({
      id_membre: "",
      mois_sosialy: currentMonth,
      annee_sosialy: currentDate.getFullYear().toString(),
      montant_sosialy: "",
      date_sosialy: currentDate.toISOString().split("T")[0],
      objet_sosialy: "Cotisation mensuelle",
    });
    setIsEditing(false);
    setIsFormModalOpen(true);
  };

  const openEditModal = (sosialyItem) => {
    setCurrentSosialy({
      id_sosialy: sosialyItem.id_sosialy,
      id_membre: sosialyItem.membre ? sosialyItem.membre.id_membre : "",
      mois_sosialy: sosialyItem.mois_sosialy,
      annee_sosialy: sosialyItem.annee_sosialy,
      montant_sosialy: sosialyItem.montant_sosialy,
      date_sosialy: sosialyItem.date_sosialy,
      objet_sosialy: sosialyItem.objet_sosialy,
    });
    setIsEditing(true);
    setIsFormModalOpen(true);
  };

  const openDeleteModal = (sosialyItem) => {
    setCurrentSosialy(sosialyItem);
    setIsDeleteModalOpen(true);
  };

  const openDetailModal = (sosialyItem) => {
    setCurrentSosialy(sosialyItem);
    setIsDetailModalOpen(true);
  };

  const handleSubmitForm = async (formData) => {
    setLoading(true);
    try {
      if (isEditing) {
        await SosialyServiceInstance.update(
          currentSosialy.id_sosialy,
          formData
        );
      } else {
        await SosialyServiceInstance.create(formData);
      }

      fetchSosialy();
      setIsFormModalOpen(false);
      return { success: true };
    } catch (error) {
      return {
        success: false,
        errors: error.response?.data?.errors || {
          general: isEditing
            ? "Impossible de modifier la cotisation. Veuillez réessayer."
            : "Impossible de créer la cotisation. Veuillez réessayer.",
        },
      };
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    setLoading(true);
    try {
      await SosialyServiceInstance.delete(currentSosialy.id_sosialy);
      fetchSosialy();
      setIsDeleteModalOpen(false);
    } catch (error) {
      setError("Impossible de supprimer la cotisation. Veuillez réessayer.");
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (newFilters) => {
    setFilters({ ...filters, ...newFilters });
  };

  const resetFilters = () => {
    setFilters({
      mois: "",
      annee: "",
      dateDebut: "",
      dateFin: "",
      montantMin: "",
      montantMax: "",
      membre: "",
    });
    setSearchTerm("");
  };

  // Calculer les statistiques
  const calculerStats = () => {
    const totalMontant = filteredSosialy.reduce(
      (acc, item) => acc + parseFloat(item.montant_sosialy || 0),
      0
    );
    const nombreCotisations = filteredSosialy.length;
    const moisUniques = [
      ...new Set(filteredSosialy.map((item) => item.mois_sosialy)),
    ].length;
    const anneeUniques = [
      ...new Set(filteredSosialy.map((item) => item.annee_sosialy)),
    ].length;

    return {
      totalMontant,
      nombreCotisations,
      moisUniques,
      anneeUniques,
    };
  };

  const stats = calculerStats();

  return (
    <div className="min-h-screen bg-gray-50 px-5 py-8 md:px-8">
      <HeaderSection
        title="Gestion des Cotisations (Sosialy)"
        description="Suivez et gérez les cotisations des membres"
        accentColor="#10B981"
      />

      {/* Statistiques */}
      <div className="mb-8 grid grid-cols-1 gap-4 sm:grid-cols-4">
        <div className="rounded-md border border-green-200 bg-green-50 p-4">
          <h3 className="text-sm font-medium text-green-900">
            Total des cotisations
          </h3>
          <p className="mt-2 text-xl font-bold text-green-700">
            {new Intl.NumberFormat("fr-MG", {
              style: "currency",
              currency: "MGA",
              minimumFractionDigits: 0,
              maximumFractionDigits: 0,
            }).format(stats.totalMontant)}
          </p>
        </div>

        <div className="bg-emerald-50 border-emerald-200 rounded-md border p-4">
          <h3 className="text-emerald-900 text-sm font-medium">
            Nombre de cotisations
          </h3>
          <p className="text-emerald-700 mt-2 text-xl font-bold">
            {stats.nombreCotisations}
          </p>
        </div>

        <div className="rounded-md border border-teal-200 bg-teal-50 p-4">
          <h3 className="text-sm font-medium text-teal-900">Mois couverts</h3>
          <p className="mt-2 text-xl font-bold text-teal-700">
            {stats.moisUniques}
          </p>
        </div>

        <div className="rounded-md border border-cyan-200 bg-cyan-50 p-4">
          <h3 className="text-sm font-medium text-cyan-900">
            Années couvertes
          </h3>
          <p className="mt-2 text-xl font-bold text-cyan-700">
            {stats.anneeUniques}
          </p>
        </div>
      </div>

      <ActionBar
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        searchPlaceholder="Rechercher par membre ou objet..."
        searchIcon={MdAttachMoney}
        onRefresh={fetchSosialy}
        onCreateNew={openCreateModal}
        buttonLabel="Nouvelle Cotisation"
        loading={loading}
        primaryColor="#10B981"
        hoverColor="#059669"
        customActions={
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center gap-2 rounded-md bg-gray-100 px-3 py-2 text-gray-700 transition-colors hover:bg-gray-200"
          >
            <MdFilterList className="h-5 w-5" />
            <span>Filtres</span>
            <span className="rounded-full bg-gray-200 px-2 py-0.5 text-xs">
              {
                Object.values(filters).filter((v) => v !== null && v !== "")
                  .length
              }
            </span>
          </button>
        }
      />

      {showFilters && (
        <SosialyFilters
          filters={filters}
          onFilterChange={handleFilterChange}
          onResetFilters={resetFilters}
          membres={membres}
          moisOptions={moisOptions}
        />
      )}

      {error && (
        <div
          className="mb-6 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-600"
          role="alert"
        >
          {error}
        </div>
      )}

      <SosialyTable
        sosialy={filteredSosialy}
        loading={loading}
        onEdit={openEditModal}
        onDelete={openDeleteModal}
        onView={openDetailModal}
      />

      <SosialyFormModal
        isOpen={isFormModalOpen}
        onClose={() => setIsFormModalOpen(false)}
        onSubmit={handleSubmitForm}
        initialSosialy={currentSosialy}
        isEditing={isEditing}
        membres={membres}
        membresLoading={membresLoading}
        moisOptions={moisOptions}
        loading={loading}
      />

      <DeleteConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={handleDelete}
        item={currentSosialy}
        itemName={currentSosialy.objet_sosialy}
        itemType="cotisation"
        warning="La suppression de cette cotisation affectera les statistiques financières."
        loading={loading}
      />

      <SosialyDetailModal
        isOpen={isDetailModalOpen}
        onClose={() => setIsDetailModalOpen(false)}
        sosialy={currentSosialy}
        onEdit={() => {
          setIsDetailModalOpen(false);
          setTimeout(() => openEditModal(currentSosialy), 300);
        }}
        onDelete={() => {
          setIsDetailModalOpen(false);
          setTimeout(() => openDeleteModal(currentSosialy), 300);
        }}
      />
    </div>
  );
};

export default SosialyManager;
